/**
 * Notifications data shapes (HS-1443; shared by the portal and admin since HS-1548).
 *
 * The DTOs mirror the backend's own feature document, `DOCS/features/portal-my-notifications.md` in
 * the AdminAPI repository, field for field. The feed is the dashboard's delivery resource extended —
 * one feed, not two — so the delivery fields the dashboard reads are carried here too, even though
 * this screen does not render them.
 *
 * The view-model half is what components are allowed to see. Both survived the move onto the live
 * API in HS-1504: only `notifications.api.ts` changed.
 */
import type { IconName } from '@smartsash/ui';

/* ------------------------------------------------------------------ *
 * Vocabularies — contract §2.1, §2.3
 * ------------------------------------------------------------------ */

/**
 * Every kind the apps have copy for. The wire type is `string` because a kind added by the
 * backend later must not crash the list; the adapter renders it neutrally.
 */
export const NOTIFICATION_KINDS = [
  'sessionFlagged',
  'sessionSummary',
  'scoreRecalculated',
  'sashIqBandChanged',
  'accessExpiring',
  'accessExpired',
  'extensionApproved',
  'extensionRejected',
  'certificationDue',
  'certificationExpired',
  'maintenance',
  'announcement',
  'system',
  // HS-1548. The two admin-side kinds reach the portal feed only for a user who is also an
  // administrator; the portal renders them as text with no link (Decision 7, through its
  // `kindsWithoutLink`), because the request is not theirs there and its portal page would 404.
  // Admin links all three to its own request page.
  'serviceRequestCreated',
  'serviceRequestWithdrawn',
  'serviceRequestResolved',
  // HS-1542. Sent to the administrator a request was just assigned to. Admin links it to the
  // request page; the portal lists it as text (`kindsWithoutLink`), like the other admin-side kinds.
  'serviceRequestAssigned',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_LINK_TARGETS = [
  'session',
  'access',
  'sashIq',
  'certification',
  'document',
  'serviceRequest',
] as const;
export type NotificationLinkTarget = (typeof NOTIFICATION_LINK_TARGETS)[number];

/** The Status filter's three values, in the order the design's dropdown lists them. */
export const NOTIFICATION_STATUSES = ['all', 'read', 'unread'] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

/* ------------------------------------------------------------------ *
 * DTOs — contract §2, §3
 * ------------------------------------------------------------------ */

export type NotificationSeverityDto = 'info' | 'warning' | 'critical';

/** The surface the backend chose; the dashboard's toasts and modals read it, this list does not. */
export type NotificationDeliveryDto = 'modal' | 'toast' | 'silent';

/** The file a `document` link opens. Same shape as the portal dashboard's own copy. */
export interface NotificationDocumentDto {
  id: string;
  name: string;
  url: string;
}

export type NotificationParamValueDto = string | number;

/** A system message: the portal owns the copy, the backend sends only the facts (§2.2). */
export interface NotificationKeyMessageDto {
  type: 'key';
  titleKey: string;
  params: Record<string, NotificationParamValueDto>;
}

/** A person's message (an announcement): shown verbatim (§2.2). */
export interface NotificationTextMessageDto {
  type: 'text';
  title: string;
  body: string;
}

export type NotificationMessageDto = NotificationKeyMessageDto | NotificationTextMessageDto;

export interface NotificationLinkDto {
  target: string;
  id?: string;
}

export interface NotificationItemDto {
  id: string;
  kind: string;
  severity: NotificationSeverityDto;
  delivery: NotificationDeliveryDto;
  createdAt: string;
  /** `null` → unread. */
  readAt: string | null;
  message: NotificationMessageDto;
  link: NotificationLinkDto | null;
  requiresAcknowledgement: boolean;
  acknowledgedAt: string | null;
  dismissedAt: string | null;
  document: NotificationDocumentDto | null;
}

export interface NotificationPageDto {
  items: NotificationItemDto[];
  /** `null` → the last page. */
  nextCursor: string | null;
  /** The caller's unread total, ignoring every filter — the badge's number (§4). */
  unreadCount: number;
  /** Rows matching this query. */
  totalCount: number;
}

export interface NotificationListParams {
  status?: NotificationStatus;
  kind?: NotificationKind[];
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface UnreadCountDto {
  count: number;
}

export interface MarkAllReadDto {
  unreadCount: number;
}

/* ================================================================== *
 * View models — what components are allowed to see
 * ================================================================== */

/** The featured icon's colour family. `neutral` is the design's white, bordered box. */
export type NotificationTone = 'error' | 'warning' | 'success' | 'neutral';

/** The badge beside the title — severity in words, so it is never conveyed by colour alone. */
export type NotificationBadge = 'actionNeeded' | 'info';

/**
 * One interpolation value, already classified so the component only formats:
 * an instant is formatted by the copy's own `datetime` specifier, a term is translated first.
 */
export type NotificationParamView =
  | { type: 'date'; value: Date }
  | { type: 'number'; value: number }
  | { type: 'text'; value: string }
  | { type: 'term'; key: string };

export type NotificationMessageView =
  | {
      type: 'key';
      /** Full i18n paths, e.g. `notifications.kinds.accessExpiring.title`. */
      titleKey: string;
      bodyKey: string;
      params: Record<string, NotificationParamView>;
    }
  | { type: 'text'; title: string; body: string };

export interface NotificationLinkView {
  target: NotificationLinkTarget;
  /** Required by `session`, `document` and `serviceRequest`; `null` for the others. */
  id: string | null;
}

export interface NotificationView {
  id: string;
  kind: NotificationKind | 'unknown';
  tone: NotificationTone;
  icon: IconName;
  badge: NotificationBadge;
  createdAt: Date;
  unread: boolean;
  message: NotificationMessageView;
  link: NotificationLinkView | null;
  /** For a `document` link: where the file opens. */
  documentUrl: string | null;
}

export interface NotificationPageView {
  items: NotificationView[];
  nextCursor: string | null;
  unreadCount: number;
  totalCount: number;
}

export type NotificationGroupKey = 'today' | 'thisWeek' | 'earlier';

export interface NotificationGroupView {
  key: NotificationGroupKey;
  items: NotificationView[];
}
